/**
 * Экран героини: канон сверху, слева бриф → промпт → запуск, справа лента запусков.
 *
 * `?image=` открывает картинку крупно (`ImageViewer` справа), `?prompt=` выбирает
 * версию промпта. Пока в очереди есть задачи, `usePersona` сам перечитывает героиню.
 */
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useParams, useSearchParams } from "react-router";

import { BriefPanel } from "@/components/persona/BriefPanel";
import { CanonBlock } from "@/components/persona/CanonBlock";
import { ImageViewer } from "@/components/persona/ImageViewer";
import { LaunchPanel, type LaunchMode } from "@/components/persona/LaunchPanel";
import { PersonaGuide } from "@/components/persona/PersonaGuide";
import { PromptPanel } from "@/components/persona/PromptPanel";
import { RunsFeed } from "@/components/persona/RunsFeed";
import { Crumbs, PageState } from "@/components/project/ui";
import { personaKeys, usePersona } from "@/lib/persona";
import { errorText } from "@/lib/project";

const numParam = (v: string | null): number | null => {
  const n = Number(v);
  return v !== null && Number.isFinite(n) && n > 0 ? n : null;
};

export function PersonaPage() {
  const id = Number(useParams().personaId);
  const client = useQueryClient();
  const [params] = useSearchParams();
  const query = usePersona(id);
  const [mode, setMode] = useState<LaunchMode>("portrait");
  const [promptPick, setPromptPick] = useState<number | null>(numParam(params.get("prompt")));
  const [viewer, setViewer] = useState<number | null>(numParam(params.get("image")));
  const view = query.data;

  if (!view) return <PageState loading={query.isPending} error={query.error ? errorText(query.error) : null} />;

  const reload = () =>
    Promise.all([
      client.invalidateQueries({ queryKey: personaKeys.one(id) }),
      client.invalidateQueries({ queryKey: personaKeys.list }),
    ]);

  const latest = view.prompts.reduce<number | null>(
    (best, p) => (best === null || p.version > (view.prompts.find((x) => x.id === best)?.version ?? 0) ? p.id : best),
    null,
  );
  const selectedId = view.prompts.some((p) => p.id === promptPick) ? promptPick : latest;

  return (
    <div className={viewer !== null ? "p-5 xl:pr-[460px]" : "p-5"}>
      <Crumbs items={[{ label: "персонажи", to: "/personas" }, { label: view.name }]} />
      <h1 className="mb-4 text-[20px] font-semibold tracking-[-0.01em] text-fg" data-annot={`героиня ${view.id}`}>
        {view.name}
      </h1>

      <div className="grid gap-4">
        <PersonaGuide open={view.runs.length === 0} />
        <CanonBlock view={view} onSheet={() => setMode("sheet")} onRebuild={() => setMode("rebuild")} />
        <div className="grid items-start gap-4 lg:grid-cols-[minmax(360px,480px)_minmax(0,1fr)]">
          <div className="grid gap-4">
            <BriefPanel key={view.id} pid={id} brief={view.brief} onSaved={() => void reload()} />
            <PromptPanel
              pid={id}
              prompts={view.prompts}
              selectedId={selectedId}
              onSelect={setPromptPick}
              onComposed={() => void reload()}
            />
            <LaunchPanel
              key={mode}
              pid={id}
              mode={mode}
              promptId={selectedId}
              models={view.models}
              onLaunched={() => {
                setMode("portrait");
                void reload();
              }}
              onCancelMode={() => setMode("portrait")}
            />
          </div>
          <div className="min-w-0">
            <RunsFeed view={view} openImage={viewer} onOpen={setViewer} />
          </div>
        </div>
      </div>
      {viewer !== null ? (
        <ImageViewer
          key={viewer}
          pid={id}
          imageId={viewer}
          models={view.models}
          onChange={setViewer}
          onChanged={reload}
        />
      ) : null}
    </div>
  );
}
